import { Component, OnInit, OnDestroy } from '@angular/core';
import { take, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { Project } from 'src/app/models/project.model';
import { Wagon } from 'src/app/models/wagon.model';
import { RequestService } from 'src/app/services/request.service';
import { AuthService } from 'src/app/services/auth.service';
import { dynamicLocaleCompareWithOptions } from 'src/app/helper/sortFunctions';
import { ModalDialog } from 'src/app/Modal/modal';
import { WaitForModalResult } from 'src/app/helper/modalHelper';
import { VariantEvaluationService } from '../VariantEvaluation.service';
import { Region } from 'src/app/models/region.model';
import { GraphicSettings } from 'src/app/models/GraphicSettings.model';
import { Graphic } from 'src/app/models/graphic.model';
import { VariantEvaluationViewParameters } from 'src/app/models/VariantEvaluationViewParameters.model';
import { DoorForChecklist, VariantAndDoorsForChecklist } from 'src/app/models/VariantAndDoorsForChecklist.model';
import { Variant } from 'src/app/models/variant.model';
import { BaseComponent } from 'src/app/models/components/BaseComponent';
import { IdentificationComponent } from 'src/app/models/components/commonComponents/IdentificationComponent';
import { HttpResponse } from '@angular/common/http';
import { downloadFile, getCheckedState, getIntermediateState, indexOfField } from '../EvaluationHelper';

@Component({
  selector: 'app-evaluation-variant',
  templateUrl: './evaluation-variant.component.html',
  styleUrls: ['./evaluation-variant.component.scss'],
  providers: [VariantEvaluationService]
})
export class EvaluationVariantComponent implements OnInit, OnDestroy {
  private destroy$: Subject<void> = new Subject();
  private companyId: number;
  private backButtonModalMessage: string = "Are your sure that you want to leave the analysis?";

  public projects: Project[];
  public wagons: Wagon[];
  public selectedProjectID: number;
  public selectedWagonID: number;
  public hasSecondFloor: boolean = false;
  public isWagonSelected: boolean = false;
  public variantWithDoorsChecklist: VariantAndDoorsForChecklist[];
  public luggageCalculationInputSource = [{ Value: 1, Name: 'Analyse across decks' }, { Value: 2, Name: 'Analyse decks separate' }];
  public luggageCalculationInputTitel = 'Luggage Calculation Option';
  public variantCollidingObjectInfo: string = "Colliding interior found";
  public layoutsUnavailableText = "This carriage has no available layouts, please choose a other carriage";
  public wagonUnavailableText = "Please select a carriage to see selectable layouts";
  public projectDropdownTitle = "Choose a project";
  public wagonDropdownTitle = "Choose a carriage";
  public analyseButtonText = "Analyse";
  public tabTravelmixTitle = "Travelmix";
  public tabGraphicsTitle = "Graphics";
  public tabVariantsTitle = "Layouts";
  public tabAdvancedSettingsTitle = "Advanced settings";

  constructor(private requestService: RequestService,
    private dialog: MatDialog,
    private authService: AuthService,
    public evaluationService: VariantEvaluationService) {
    this.companyId = this.authService.getCurrentCompanyId();
  }

  ngOnInit() {
    this.evaluationService.initializeEvaluationFrontendParameter();
    this.loadProjects();
    this.loadRegions(this.companyId);
    this.loadGraphics(this.companyId);
    this.loadMasterData();

    this.evaluationService.project
      .pipe(takeUntil(this.destroy$))
      .subscribe(next => {
        if (next != null && next.id > 0) {
          this.requestService.getWagons(next.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe(next => {
              next.sort((a, b) => dynamicLocaleCompareWithOptions(a, b, 'name'));
              this.wagons = next;
            });
        }
      });

    this.evaluationService.entity
      .pipe(takeUntil(this.destroy$))
      .subscribe((wagon: Wagon) => {
        this.isWagonSelected = wagon ? true : false;
        if (wagon !== undefined && wagon !== null) {
          this.updateVariants(wagon);
        } else {
          this.variantWithDoorsChecklist = null;
          this.evaluationService.evaluationFrontEndParameter.variantAndDoorList = new Array<VariantEvaluationViewParameters>();
        }
      });

    this.evaluationService.passengerAlgorithmGraphicSelected
      .pipe(takeUntil(this.destroy$))
      .subscribe(passengerAlgorithmGraphicSelected => {
        if (passengerAlgorithmGraphicSelected && this.evaluationService.evaluationFrontEndParameter.variantAndDoorList && this.evaluationService.evaluationFrontEndParameter.variantAndDoorList.length > 0) {
          this.evaluationService.evaluationFrontEndParameter.variantAndDoorList.forEach(v => {
            let tmp = this.variantWithDoorsChecklist.find(x => x.id == v.id);
            if (tmp && tmp.isSelected && !tmp.doors) {
              this.loadDoors(tmp, tmp.isSelected);
            }
          })
        }
      });
  }

  debug() {
    console.log("Debug component: ", this);
  }

  loadProjects() {
    this.requestService.getProjects(this.companyId)
      .pipe(take(1))
      .subscribe(resp => {
        resp.sort((a, b) => dynamicLocaleCompareWithOptions(a, b, 'name'));
        this.projects = resp;
      });
  }

  loadMasterData() {
    this.requestService.getMasterData()
      .pipe(takeUntil(this.destroy$))
      .subscribe(masterData => {
        masterData.comfortTypeList.sort((a, b) => dynamicLocaleCompareWithOptions(a, b, 'name'));
        this.evaluationService.comfortValues = masterData.comfortTypeList;
        masterData.transportModeList.sort((a, b) => dynamicLocaleCompareWithOptions(a, b, 'name'));
        this.evaluationService.transportModes = masterData.transportModeList;
        this.evaluationService.allGraphics = masterData.graphicList;
      });
  }

  loadRegions(companyId: number): any {
    this.requestService.getCompanyRegions(companyId)
      .pipe(take(1))
      .subscribe((resp: Region[]) => {
        resp.sort((a, b) => dynamicLocaleCompareWithOptions(a, b, 'name'));
        this.evaluationService.regions = resp;
      });
  }

  loadGraphics(companyId: number) {
    this.requestService.getGraphicSettings(companyId)
      .pipe(take(1))
      .subscribe((resp: GraphicSettings) => {
        if (resp) {
          this.evaluationService.companyGraphicSettings = resp;
        }
        else {
          console.error("No graphics available, defaulting to empty graphics array!");
          this.evaluationService.companyGraphicSettings = new GraphicSettings();
          this.evaluationService.companyGraphicSettings.settings = new Array<Graphic>();
        }
      });
  }

  loadDoors(variant: VariantAndDoorsForChecklist, status) {
    if (status) {
      if (!variant.doors && this.evaluationService.passengerAlgorithmGraphicSelected.value) {
        this.requestService.getVariantWithDoors(variant.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe((variantWithDoors: VariantEvaluationViewParameters) => {
            let mappedDoors = this.mapDoorsForChecklist(variantWithDoors.doors);
            variant.doors = mappedDoors.sort((a, b) => dynamicLocaleCompareWithOptions(a, b, 'userId'));;
          });
      }
    }
  }

  updateVariants(wagon: Wagon) {
    this.requestService.getVariants(wagon.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((variants: Variant[]) => {
        this.mapVariantForChecklist(variants);
        this.resetLuggageCalculationInput(wagon);
      });
  }

  changeProjectValue(value) {
    if (value !== undefined) {
      const project = this.projects.find(p => p.id == value);
      this.evaluationService.project.next(project);
      this.evaluationService.entity.next(null);
      this.selectedProjectID = value;
    } else {
      this.evaluationService.project.next(null);
      this.evaluationService.entity.next(null);
      this.wagons = null;
      this.selectedWagonID = null;
      this.selectedProjectID = null;
    }

    this.evaluationService.evaluationFrontEndParameter.variantAndDoorList = [];
    this.hasSecondFloor = false;
  }

  changeLuggageCalculationInput(value) {
    if (value == 1) {
      this.evaluationService.evaluationFrontEndParameter.considerAllDecksForCalculation = true;
    } else if (value == 2) {
      this.evaluationService.evaluationFrontEndParameter.considerAllDecksForCalculation = false;
    }
  }

  changeWagon(value) {
    if (value !== undefined) {
      const wagon = this.wagons.find(w => w.id == value);
      this.evaluationService.entity.next(wagon);
      this.selectedWagonID = value;
    } else {
      this.evaluationService.entity.next(null);
      this.selectedWagonID = null;
      this.hasSecondFloor = false;
    }

    this.isWagonSelected = this.selectedWagonID ? true : false;
    this.evaluationService.evaluationFrontEndParameter.variantAndDoorList = [];
  }

  mapVariantForChecklist(variants: Variant[]) {
    this.variantWithDoorsChecklist = null;

    if (variants != null) {
      this.variantWithDoorsChecklist = new Array<VariantAndDoorsForChecklist>();

      for (let variantIndex = 0; variantIndex < variants.length; variantIndex++) {
        const variant = variants[variantIndex];
        let vadfc: VariantAndDoorsForChecklist = new VariantAndDoorsForChecklist();
        vadfc.isSelected = false;
        vadfc.id = variant.id;
        vadfc.name = variant.name;
        vadfc.hasCollissions = variant.hasCollissions;
        this.variantWithDoorsChecklist.push(vadfc)
      }
      // sort variants after mapping finished
      this.variantWithDoorsChecklist.sort((a, b) => dynamicLocaleCompareWithOptions(a, b, 'name'));
    }
  }

  mapDoorsForChecklist(originalDoors: Array<any>): DoorForChecklist[] {
    let doors: DoorForChecklist[] = new Array<DoorForChecklist>();
    for (let doorIndex = 0; doorIndex < originalDoors.length; doorIndex++) {
      const currentDoor = originalDoors[doorIndex];
      let mapedDoor: DoorForChecklist = new DoorForChecklist();
      mapedDoor.isSelected = false;
      mapedDoor.id = currentDoor.doorIdentificationComponent.id;
      mapedDoor.userId = currentDoor.doorIdentificationComponent.userId;
      mapedDoor.isUpperDoor = currentDoor.isUpperDoor;

      doors.push(mapedDoor);
    }
    return doors;
  }

  updateChild(variant: VariantAndDoorsForChecklist, status) {
    if (!status) {
      if (variant.doors) {
        for (let doorIndex = 0; doorIndex < variant.doors.length; doorIndex++) {
          const door = variant.doors[doorIndex];
          door.isSelected = variant.isSelected;
        }
      }
      let index = indexOfField(this.evaluationService.evaluationFrontEndParameter.variantAndDoorList, 'id', variant.id);
      this.evaluationService.evaluationFrontEndParameter.variantAndDoorList.splice(index, 1);
    }
    else {
      let v = new VariantEvaluationViewParameters();
      v.id = variant.id;
      v.doors = new Array<IdentificationComponent>();
      v.hasCollissions = variant.hasCollissions;
      this.evaluationService.evaluationFrontEndParameter.variantAndDoorList.push(v);
    }
  }

  syncVariantParamObject(variant: VariantAndDoorsForChecklist) {
    let vIndex = indexOfField(this.evaluationService.evaluationFrontEndParameter.variantAndDoorList, 'id', variant.id);
    const selectedVariant = this.evaluationService.evaluationFrontEndParameter.variantAndDoorList[vIndex];
    selectedVariant.doors = [];

    variant.doors.forEach(door => {
      if (door.isSelected) {
        let identificationComponent = new IdentificationComponent();
        identificationComponent.id = door.id;
        identificationComponent.userId = door.userId;

        selectedVariant.doors.push(identificationComponent);
      }
    });
  }

  setDoorsSelectedState(variant: VariantAndDoorsForChecklist, upperDoor: boolean, checkedStatus: boolean) {
    variant.doors.forEach(door => {
      if (door.isUpperDoor && upperDoor) {
        if (!door.isSelected && checkedStatus || door.isSelected && !checkedStatus) {
          door.isSelected = checkedStatus;
        }
      }
      else if (!door.isUpperDoor && !upperDoor) {
        if (!door.isSelected && checkedStatus || door.isSelected && !checkedStatus) {
          door.isSelected = checkedStatus;
        }
      }
    });
    this.syncVariantParamObject(variant);
  }

  public getCheckedState(doors: DoorForChecklist[], upperDoor: boolean) {
    return getCheckedState(doors, upperDoor);
  }

  public getIntermediateState(doors: DoorForChecklist[], upperDoor: boolean) {
    return getIntermediateState(doors, upperDoor);
  }

  resetLuggageCalculationInput(wagon: Wagon) {
    if (wagon.floorCount == 2) {
      this.hasSecondFloor = true;
    } else {
      this.hasSecondFloor = false;
    }
    this.evaluationService.evaluationFrontEndParameter.considerAllDecksForCalculation = false;
  }

  evaluate() {
    this.requestService.evaluateVariant(this.evaluationService.evaluationFrontEndParameter)
      .subscribe((resp: HttpResponse<Blob>) => {
        downloadFile(resp.body, resp.headers.get('Content-Disposition'));
      });
  }

  validateParameters(): boolean {
    let disabled = false;

    if (this.evaluationService.evaluationFrontEndParameter.projectId == undefined || this.evaluationService.evaluationFrontEndParameter.projectId == null || this.evaluationService.evaluationFrontEndParameter.projectId < 1) {
      return true;
    }

    // travel mix list has to have at least one entry, if there should be a restriction for total travel mixes this has to be added.
    if (this.evaluationService.evaluationFrontEndParameter.travelMixList == undefined || this.evaluationService.evaluationFrontEndParameter.travelMixList == null || this.evaluationService.evaluationFrontEndParameter.travelMixList.length == 0) {
      return true;
    }

    // min selection must be 1 variant atleast
    if (this.evaluationService.evaluationFrontEndParameter.variantAndDoorList == undefined || this.evaluationService.evaluationFrontEndParameter.variantAndDoorList == null || this.evaluationService.evaluationFrontEndParameter.variantAndDoorList.length == 0) {
      return true;
    }

    if (this.evaluationService.evaluationFrontEndParameter.graphicList == undefined || this.evaluationService.evaluationFrontEndParameter.graphicList == null || this.evaluationService.evaluationFrontEndParameter.graphicList.length == 0) {
      return true;
    }

    let result = this.evaluationService.companyGraphicSettings.settings.filter(e => {
      let res = (this.evaluationService.evaluationFrontEndParameter.graphicList.includes(e.graphicId)) && e.algorithmId == 2;
      return res;
    }).length > 0;

    // when a passenger exchange graphic was selected there has to be atleast one door selected
    if (result) {
      let res = (this.evaluationService.evaluationFrontEndParameter.variantAndDoorList.filter(x => x.doors.length > 0).length > 0);
      if (!res) {
        return true;
      }
    }

    if (this.evaluationService.evaluationFrontEndParameter.advancedSettings.default.personsBoardingDefault === null ||
      this.evaluationService.evaluationFrontEndParameter.advancedSettings.default.personsBoardingDefault < 0) {
      return true;
    }
    if (this.evaluationService.evaluationFrontEndParameter.advancedSettings.default.personsDeboardingDefault === null ||
      this.evaluationService.evaluationFrontEndParameter.advancedSettings.default.personsDeboardingDefault < 0) {
      return true;
    }

    if (this.evaluationService.evaluationFrontEndParameter.advancedSettings.default.personValueFormatId === 1) {

      if (this.evaluationService.evaluationFrontEndParameter.advancedSettings.default.personsBoardingDefault > 100) {
        return true;
      }

      if (this.evaluationService.evaluationFrontEndParameter.advancedSettings.default.personsDeboardingDefault > 100) {
        return true;
      }
    }

    return disabled;
  }

  canDeactivate() {
    const dialogRef = this.dialog.open(ModalDialog, {
      data: {
        title: 'Warning',
        message: this.backButtonModalMessage,
        showCancel: true,
        cancelButtonText: 'Cancel',
        saveButtonText: 'Leave'
      }
    });
    return WaitForModalResult(dialogRef);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
